import React, { useEffect, useMemo, useRef, useState } from "react";
import { Trans } from "@lingui/macro";
import PropTypes from "prop-types";

import { RichTextEditor, plugins, FORMAT_TYPES } from "widgets/richtext";
import { Button, BUTTON_TYPES, BUTTON_SIZES } from "widgets/inputs";
import { FilePicker } from "kfplatform/filePicker";
import { useAccountStore } from "base/stores";
import { generateId } from "base/utils";
import { Icon } from "widgets/datadisplay";
import { Tooltip, TOOLTIP_TYPES, TOOLTIP_POSITION } from "widgets/feedback";
import { EmojiGifPicker } from "widgets/emojigifpicker";

import { Embedurl } from "../../components/post/embedurl";
import { ForumService } from "../../common/service/forum.service";
import { useForumStore } from "../../common/store/forum.store";
import { useChannelStore } from "@/common/store/channel.store";
import { formatFileHandler, isNoContent } from "@/common/utils/utils";
import { MentionList } from "../mention_list";
import {
  CHANNEL_TYPES,
  SPECIAL_MENTION_TYPES,
  CHANNEL_CONST
} from "../../common/utils/constant";
import { CommentQuote } from "./comment.quote";
import { CommentAttachments } from "./comment.attachments";

import styles from "../../forum/forum.css";

import EmojiIcon from "../../common/icons/EmojiPlus.svg";
import AttachmentIcon from "../../icons/ReplyAttachment.svg";

export function CommentBox({
  topicId,
  focus,
  value,
  isEdit,
  commentId,
  setEdit,
  setEditorFocus,
  commentAttachments,
  teamId,
  comment
}) {
  const [isPickerOpen, setPickerOpen] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [editorValue, setEditorValue] = useState([]);
  const [noattachment, setnoattachment] = useState(false);
  const editorRef = useRef();
  const accountId = useAccountStore.getState()._id;
  const [attachments, setAttachments] = useState({ images: [], files: [] });
  const { mediumId } = useChannelStore((state) => ({
    mediumId: state.mediumId
  }));
  const {
    topicData,
    replyComment = {},
    quotedContent = {}
  } = useForumStore((state) => ({
    topicData: state.topicData[topicId],
    replyComment: state.replyComment,
    quotedContent: state.quotedContent
  }));

  useEffect(
    function setFocus() {
      if (focus) {
        scrollTo();
      }
    },
    [focus]
  );

  useEffect(
    function resetComment() {
      resetState();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [topicId]
  );

  useEffect(
    function setValue() {
      setEditorValue(value);
    },
    [value]
  );
  useEffect(
    function setFiles() {
      if (commentAttachments) {
        setAttachments({
          images: commentAttachments.images,
          files: commentAttachments.files
        });
      }
    },
    [commentAttachments]
  );
  const customPlugins = useMemo(function customPlugin() {
    return [
      plugins.MentionPlugin,
      plugins.BoldPlugin,
      plugins.ItalicPlugin,
      plugins.LinkPlugin,
      plugins.UnderlinePlugin,
      plugins.StrikePlugin,
      plugins.ColorPlugin,
      plugins.ImagePlugin,
      {
        formatType: FORMAT_TYPES.EMBED_LINK,
        component: React.forwardRef((prop, ref) => {
          return (
            <Embedurl innerRef={ref} {...prop} title={CHANNEL_CONST.COMMENT} />
          );
        })
      }
    ];
  }, []);

  const specialMentionsList = [
    {
      ...SPECIAL_MENTION_TYPES.Forum,
      _id: mediumId
    }
  ];

  function onChooseGif(data, isEmoji = false) {
    if (!isEmoji) {
      editorRef.current.insertEmoji(data);
    } else {
      editorRef.current.insertBlock(FORMAT_TYPES.IMAGE, { src: data });
    }
  }

  function scrollTo() {
    const scrollElement = document.getElementById("topic-content");
    scrollElement.scrollTop = scrollElement.scrollHeight;
  }

  function onClickCommentBox() {
    if (!isEdit) {
      setEditorFocus(true);
      scrollTo();
    }
  }

  function triggerTyping() {
    if (editorRef.current) {
      let getConent = editorRef.current.getContents();
      let otherContent = getConent.map((el) => {
        return el.type === "image";
      });
      otherContent.includes(true)
        ? setnoattachment(true)
        : setnoattachment(false);
    }
  }

  function resetState() {
    useForumStore.getState().setQuotedText([]);
    useForumStore.getState().setReplyComment({});
    setEditorValue([]);
    setEditorFocus(false);
    setAttachments({
      images: [],
      files: []
    });
  }

  function handleEnter() {
    if (
      !editorRef.current.isEmpty() ||
      attachments.images.length > 0 ||
      attachments.files.length > 0
    ) {
      let textContent = editorRef.current.getContents();
      if (attachments.images.length > 0 || attachments.files.length > 0) {
        const node = {
          type: FORMAT_TYPES.COLLAGE,
          data: attachments
        };
        textContent.splice(textContent.length, 0, node);
      }
      if (quotedContent.Content) {
        textContent = [...textContent, ...quotedContent.Content];
      }
      const eventId = generateId("Ev");
      let parentId = replyComment?._id
        ? { ParentCommentId: replyComment._id }
        : {};
      let data = {
        AtMention: editorRef.current.getContentsByFormatType([
          FORMAT_TYPES.MENTION
        ]),
        Content: textContent,
        ...parentId,
        EventId: eventId
      };
      useForumStore.getState().setEventId(eventId);

      setLoading(true);
      if (isEdit) {
        if (comment) {
          const quotedParent = comment.Content.find(
            (comment) => comment.type === FORMAT_TYPES.BLOCKQUOTE
          );
          if (quotedParent) {
            data.Content = [...data.Content, quotedParent];
          }
        }
        const [commentEditPromise] = ForumService.updateComment(
          teamId,
          mediumId,
          topicData?._id,
          commentId,
          data
        );
        commentEditPromise()
          .then(() => {
            useForumStore.getState().updateComments(topicId, commentId, {
              ...data,
              IsEdited: true
            });
            setLoading(false);
            setEdit(false);
            resetState();
          })
          .catch((error) => {
            setLoading(false);
            console.info(error);
          });
      } else {
        const [commentPromise] = ForumService.postComment(
          teamId,
          mediumId,
          topicData?._id,
          data
        );
        commentPromise()
          .then((result) => {
            useForumStore.getState().addComments(topicId, [result]);
            setLoading(false);
            resetState();
          })
          .catch((error) => {
            setLoading(false);
            console.info(error);
          });
      }
      setnoattachment(false);
    }
  }
  function onCancel() {
    resetState();
    if (isEdit) {
      setEdit(false);
    }
  }
  function updateImages(images) {
    let imageList = attachments.images;
    let fileList = attachments.files;
    [imageList, fileList] = formatFileHandler(images, imageList, fileList);
    setAttachments({ images: imageList, files: fileList });
  }

  function removeAttachmentItem(key) {
    setAttachments((prevstate) => {
      return {
        images: [...prevstate.images.filter((el) => el.default.key !== key)],
        files: [...prevstate.files.filter((el) => el.key !== key)]
      };
    });
  }

  return (
    <React.Fragment>
      <div className={styles.commentInputBox}>
        {(replyComment.Content || quotedContent.Content) && (
          <div className={styles.commentBoxQuote}>
            {replyComment.Content && (
              <CommentQuote
                topicId={topicId}
                quoteContent={replyComment.Content || []}
                parentComment={replyComment}
                attachments={replyComment.attachments}
                isNoText={isNoContent(replyComment.Content)}
              />
            )}
            {quotedContent.Content && (
              <CommentQuote
                topicId={topicId}
                quoteContent={quotedContent.Content || []}
                parentComment={
                  quotedContent.Content?.[0]?.data?.EntityType ===
                  CHANNEL_CONST.COMMENT
                    ? {}
                    : null
                }
              />
            )}
          </div>
        )}
        <RichTextEditor
          ref={editorRef}
          onClick={onClickCommentBox}
          className={styles.commentBoxEditor}
          value={editorValue}
          title={CHANNEL_CONST.COMMENT}
          plugins={customPlugins}
          allowSlashCommand={false}
          inlineToolbar={false}
          focus={focus}
          // onBlur={() => setEditorFocus(false)}
          placeholder={"Write a reply here"}
          onEnter={() => handleEnter()}
          onChange={triggerTyping}
          shouldCheckEmptyonEnter={
            !(attachments.images.length + attachments.files.length >= 1)
          }
          acceptedKeyStrokes={{
            "@": (props) => {
              return (
                <MentionList
                  {...props}
                  teamId={teamId}
                  medium={CHANNEL_CONST.CHANNEL}
                  mediumId={mediumId}
                  specialMentionsList={specialMentionsList}
                  channelType={CHANNEL_TYPES.FORUM}
                />
              );
            }
          }}
        />
        {focus && (
          <div className={styles.commentBoxFooter}>
            <CommentAttachments
              attachments={attachments}
              removeAttachmentItem={removeAttachmentItem}
            />
            <div className={styles.commentBoxFooterActions}>
              <div className={styles.left}>
                {attachments.images.length + attachments.files.length >= 3 ||
                noattachment ? (
                  <Icon className={`${styles.disableIcon}`}>
                    <AttachmentIcon />
                  </Icon>
                ) : (
                  <Tooltip
                    content={<Trans>Attachment</Trans>}
                    type={TOOLTIP_TYPES.SMALL}
                    position={TOOLTIP_POSITION.TOP}
                  >
                    <div>
                      <Icon onClick={() => setPickerOpen(true)}>
                        <AttachmentIcon className={styles.activeAttach} />
                      </Icon>
                    </div>
                  </Tooltip>
                )}
                <EmojiGifPicker
                  onClick={onChooseGif}
                  icon={
                    <Tooltip
                      content={<Trans>Emoji</Trans>}
                      type={TOOLTIP_TYPES.SMALL}
                      position={TOOLTIP_POSITION.TOP}
                    >
                      <EmojiIcon />
                    </Tooltip>
                  }
                />
              </div>
              <div className={styles.right}>
                <div onClick={onCancel} className={styles.cancelBtn}>
                  <Trans>Cancel</Trans>
                </div>
                <Button
                  type={BUTTON_TYPES.PRIMARY}
                  size={BUTTON_SIZES.SMALL}
                  onClick={handleEnter}
                  isLoading={isLoading}
                >
                  {isEdit ? <Trans>Update</Trans> : <Trans>Reply</Trans>}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      <FilePicker
        open={isPickerOpen}
        onPickerClose={() => setPickerOpen(false)}
        options={{
          imageProps: {
            sizes: [
              [1200, 1200],
              [600, 600],
              [300, 300],
              [75, 75]
            ]
          },
          maxCount: 3,
          maxSize: 52428800,
          fileExtensions: ["*"]
        }}
        onUploadDone={(images) => updateImages(images)}
        prefix={mediumId}
        baseURL={`/upload/2/${accountId}/`}
      />
    </React.Fragment>
  );
}

CommentBox.propTypes = {
  focus: PropTypes.string,
  mediumId: PropTypes.string,
  topicId: PropTypes.string,
  value: PropTypes.array,
  isEdit: PropTypes.bool,
  commentId: PropTypes.string,
  setEdit: PropTypes.func,
  setEditorFocus: PropTypes.func,
  commentAttachments: PropTypes.array,
  teamId: PropTypes.string,
  comment: PropTypes.object
};
